import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Search, X, Loader2, Briefcase, Settings } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { useSearchAutocomplete } from '@/hooks/useSearchAutocomplete';
import { useNavigationSearch, SearchResult } from '@/hooks/useNavigationSearch';
import SearchSuggestions from '@/components/SearchSuggestions';
import engineTurbineBase from '@/assets/engine-turbine-base.png';
import partsPackageIcon from '@/assets/parts-package-icon.png';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';

interface MarketplaceSearchInputProps {
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  value: string;
  onChange: (value: string) => void;
  onClearInput?: () => void;
  onSubmit?: (value: string) => void;
  className?: string;
  /** When true, suppresses the autocomplete/suggestions dropdown — used inside the mobile filter drawer to avoid overlap. */
  disableSuggestions?: boolean;
}

const getPlaceholder = (assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services') => {
  switch (assetType) {
    case 'helicopter':
      return 'Search helicopters, models, locations...';
    case 'engine':
      return 'Search engines, manufacturers, part numbers...';
    case 'parts':
      return 'Search parts, tools, equipment, P/N...';
    case 'jobs':
      return 'Search jobs, companies, positions...';
    case 'services':
      return 'Search services, providers, capabilities...';
  }
};

export const MarketplaceSearchInput: React.FC<MarketplaceSearchInputProps> = ({
  assetType,
  value,
  onChange,
  onClearInput,
  onSubmit,
  className,
  disableSuggestions = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { recentSearches, addSearch, clearSearches } = useRecentSearches();
  const { suggestions: autocompleteSuggestions } = useSearchAutocomplete(value);
  const { results, isLoading, hasResults } = useNavigationSearch(value);

  // Flatten all results into a single list for keyboard navigation
  const flatResults = useMemo(() => {
    const flat: SearchResult[] = [];
    if (results.helicopters.length > 0) flat.push(...results.helicopters);
    if (results.engines.length > 0) flat.push(...results.engines);
    if (results.parts.length > 0) flat.push(...results.parts);
    if (results.jobs.length > 0) flat.push(...results.jobs);
    if (results.services.length > 0) flat.push(...results.services);
    return flat;
  }, [results]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1);
  }, [flatResults.length, value]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };
    if (isFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFocused]);

  const handleSuggestionClick = (term: string) => {
    onChange(term);
    addSearch(term);
    setIsFocused(false);
  };

  // Use in-page setSearchParams instead of navigate() to avoid remounting
  const handleResultClick = useCallback((result: SearchResult) => {
    addSearch(value);
    const params = new URLSearchParams(searchParams);
    params.set('asset', result.assetType);
    params.set('listing', result.id);
    // Set asset-specific param
    if (result.assetType === 'engine') params.set('engine', result.id);
    else if (result.assetType === 'parts') params.set('parts', result.id);
    else if (result.assetType === 'jobs') params.set('job', result.id);
    else if (result.assetType === 'services') params.set('service', result.id);
    params.set('view', 'map');
    // Clear search so dropdown closes
    params.delete('search');
    setSearchParams(params, { replace: true });
    // Use onClearInput to clear text without triggering selection-clearing logic
    if (onClearInput) {
      onClearInput();
    } else {
      onChange('');
    }
    setIsFocused(false);
  }, [value, searchParams, setSearchParams, addSearch, onChange, onClearInput]);

  const handleViewAll = useCallback((targetAssetType: string) => {
    addSearch(value);
    // Sync parent's search state via onChange so debouncedSearchQuery updates
    onChange(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('search', value);
    newParams.set('asset', targetAssetType);
    // Clear stale listing params
    newParams.delete('listing');
    newParams.delete('engine');
    newParams.delete('parts');
    newParams.delete('job');
    newParams.delete('service');
    setSearchParams(newParams);
    setIsFocused(false);
  }, [value, searchParams, setSearchParams, addSearch, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const showResults = value.trim().length >= 2;

    if (showResults && flatResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(prev => {
          const next = prev < flatResults.length - 1 ? prev + 1 : 0;
          scrollActiveIntoView(next);
          return next;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(prev => {
          const next = prev > 0 ? prev - 1 : flatResults.length - 1;
          scrollActiveIntoView(next);
          return next;
        });
        return;
      }
      if (e.key === 'Enter' && activeIndex >= 0 && activeIndex < flatResults.length) {
        e.preventDefault();
        handleResultClick(flatResults[activeIndex]);
        return;
      }
    }

    if (e.key === 'Enter' && value.trim().length >= 2) {
      addSearch(value);
      // Flush search immediately to grid
      if (onSubmit) {
        onSubmit(value);
      }
      setIsFocused(false);
    }
    if (e.key === 'Escape') {
      setIsFocused(false);
    }
  };

  const scrollActiveIntoView = (index: number) => {
    requestAnimationFrame(() => {
      const container = resultsRef.current;
      if (!container) return;
      const items = container.querySelectorAll('[data-result-index]');
      const item = items[index] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    });
  };

  const showDropdown = isFocused && !disableSuggestions;
  const showResults = value.trim().length >= 2;
  const showSuggestions = !showResults;

  // Build a global index counter for keyboard nav
  let globalIndex = 0;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
      <Input
        type="text"
        placeholder={getPlaceholder(assetType)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onKeyDown={handleKeyDown}
        className="pl-9 pr-9"
        role="combobox"
        aria-expanded={showDropdown}
        aria-activedescendant={activeIndex >= 0 ? `search-result-${activeIndex}` : undefined}
      />
      {value && !isLoading && (
        <button
          onClick={() => {
            if (onSubmit) {
              onSubmit('');
            } else {
              onChange('');
            }
            if (onClearInput) {
              onClearInput();
            }
            setIsFocused(false);
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {isLoading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
      )}

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover rounded-lg shadow-xl border border-border overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50">
          {showSuggestions ? (
            <SearchSuggestions
              onSuggestionClick={handleSuggestionClick}
              recentSearches={recentSearches}
              onClearRecent={clearSearches}
              autocompleteSuggestions={autocompleteSuggestions}
              query={value}
            />
          ) : isLoading && !hasResults ? (
            <div className="p-4 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              <span className="text-sm">Searching...</span>
            </div>
          ) : (
            <div ref={resultsRef} className="max-h-[400px] overflow-y-auto" role="listbox">
              {results.helicopters.length > 0 && (
                <ResultSection
                  title="Helicopters"
                  icon={<HelicopterSilhouetteIcon className="w-4 h-4" tone="nav" />}
                  results={results.helicopters}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('helicopter')}
                  activeIndex={activeIndex}
                  startIndex={(() => { const s = globalIndex; globalIndex += results.helicopters.length; return s; })()}
                />
              )}
              {results.engines.length > 0 && (
                <ResultSection
                  title="Engines"
                  icon={<img src={engineTurbineBase} alt="" className="w-4 h-4 object-contain brightness-0 dark:invert" />}
                  results={results.engines}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('engine')}
                  activeIndex={activeIndex}
                  startIndex={(() => { const s = globalIndex; globalIndex += results.engines.length; return s; })()}
                />
              )}
              {results.parts.length > 0 && (
                <ResultSection
                  title="Parts & Tools"
                  icon={<img src={partsPackageIcon} alt="" className="w-4 h-4 object-contain brightness-0 dark:invert" />}
                  results={results.parts}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('parts')}
                  activeIndex={activeIndex}
                  startIndex={(() => { const s = globalIndex; globalIndex += results.parts.length; return s; })()}
                />
              )}
              {results.jobs.length > 0 && (
                <ResultSection
                  title="Jobs"
                  icon={<Briefcase className="w-4 h-4" />}
                  results={results.jobs}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('jobs')}
                  activeIndex={activeIndex}
                  startIndex={(() => { const s = globalIndex; globalIndex += results.jobs.length; return s; })()}
                />
              )}
              {results.services.length > 0 && (
                <ResultSection
                  title="Services"
                  icon={<Settings className="w-4 h-4" />}
                  results={results.services}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('services')}
                  activeIndex={activeIndex}
                  startIndex={(() => { const s = globalIndex; globalIndex += results.services.length; return s; })()}
                />
              )}
              {/* Zero-result recovery */}
              {!hasResults && !isLoading && (
                <div className="p-3">
                  <p className="text-center text-muted-foreground text-sm mb-3">
                    No listings found for "{value}"
                  </p>
                  {autocompleteSuggestions.length > 0 && (
                    <div className="border-t border-border/50 pt-2">
                      <SearchSuggestions
                        onSuggestionClick={handleSuggestionClick}
                        autocompleteSuggestions={autocompleteSuggestions}
                        query={value}
                      />
                    </div>
                  )}
                  {autocompleteSuggestions.length === 0 && (
                    <SearchSuggestions onSuggestionClick={handleSuggestionClick} />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Result section component
interface ResultSectionProps {
  title: string;
  icon: React.ReactNode;
  results: SearchResult[];
  onResultClick: (result: SearchResult) => void;
  onViewAll?: () => void;
  activeIndex: number;
  startIndex: number;
}

const ResultSection: React.FC<ResultSectionProps> = ({ title, icon, results, onResultClick, onViewAll, activeIndex, startIndex }) => {
  return (
    <div className="border-b border-border/50 last:border-b-0">
      <div className="px-3 py-2 bg-muted/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {title} ({results.length >= 5 ? '5+' : results.length})
          </span>
        </div>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            View all
            <Search className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="py-1">
        {results.map((result, idx) => {
          const globalIdx = startIndex + idx;
          const isActive = globalIdx === activeIndex;
          return (
            <button
              key={result.id}
              id={`search-result-${globalIdx}`}
              data-result-index={globalIdx}
              onClick={() => onResultClick(result)}
              role="option"
              aria-selected={isActive}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isActive ? "bg-accent text-accent-foreground" : "hover:bg-muted/50"
              )}
            >
              <div className="w-10 h-10 rounded-md bg-muted flex-shrink-0 overflow-hidden">
                {result.thumbnailUrl ? (
                  <img
                    src={result.thumbnailUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="w-full h-full items-center justify-center text-muted-foreground/40"
                  style={{ display: result.thumbnailUrl ? 'none' : 'flex' }}
                >
                  {result.assetType === 'helicopter' && <HelicopterSilhouetteIcon className="w-5 h-5" tone="nav" />}
                  {result.assetType === 'engine' && <img src={engineTurbineBase} alt="" className="w-5 h-5 object-contain opacity-40" />}
                  {result.assetType === 'parts' && <img src={partsPackageIcon} alt="" className="w-5 h-5 object-contain opacity-40" />}
                  {result.assetType === 'jobs' && <Briefcase className="w-5 h-5 opacity-40" />}
                  {result.assetType === 'services' && <Settings className="w-5 h-5 opacity-40" />}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-popover-foreground truncate">
                  {result.title}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {result.subtitle}
                  {result.location && ` • ${result.location}`}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
